import { publicQuoteDate } from './publicQuoteDate';
import { InsuranceCoverage } from '../travel-services/components/InsuranceCoverage';
import { useState } from 'react';
import { Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Typography, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PublicQuoteComponent } from '@bandido/shared/types';
import { AirlineLogo } from '../air/search/AirlineLogo';
import { dayOffset, dayOffsetLabel, formatDuration, localTime } from '../air/search/airSearch';
import { shiftQuoteDate } from '@bandido/shared/commercial-quotes';
export function PublicQuoteDetails({ component: c, showTitle = true }: { component: PublicQuoteComponent; showTitle?: boolean }) {
  const [photoOpen, setPhotoOpen] = useState(false);
  const [failedImage, setFailedImage] = useState<string>();
  const { t, i18n } = useTranslation(),
    tr = (k: string, values?: Record<string, string | number>) => t(`commercialQuotes.publicView.${k}`, values),
    {flight} = c;
  const {location} = c,
    point =
      location && typeof location.latitude === 'number' && typeof location.longitude === 'number'
        ? `${location.latitude},${location.longitude}`
        : location?.address;
  const hotel = c.serviceType === 'hotel';
  const hasPhoto = Boolean(c.imageUrl && failedImage !== c.imageUrl);
  const mapUrl = point ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(point)}` : undefined;
  return (
    <Stack spacing={1.5} sx={{ minWidth: 0 }}>
      <Stack direction={hotel ? { xs: 'column', sm: 'row' } : 'row'} spacing={2} alignItems={hotel ? 'stretch' : 'center'}>
        {(c.imageUrl || hotel) && <Box sx={{ width: hotel ? { xs: '100%', sm: 200 } : 112, flexShrink: 0 }}>
          {hasPhoto ? <Box component={hotel ? 'button' : 'div'} {...(hotel ? {type: 'button', onClick: () => setPhotoOpen(true), 'aria-label': tr('viewHotelPhoto')} : {})} sx={{ display: 'block', width: '100%', p: 0, border: 0, bgcolor: 'transparent', cursor: hotel ? 'pointer' : 'default', borderRadius: 2 }}>
            <Box component="img" src={c.imageUrl} onError={() => setFailedImage(c.imageUrl)} alt={c.title} loading="lazy" referrerPolicy="no-referrer" sx={{ display: 'block', width: '100%', height: hotel ? 160 : 88, objectFit: 'cover', borderRadius: 2 }} />
          </Box> : <Box sx={{ minHeight: hotel ? 140 : 88, bgcolor: 'grey.100', p: 2, borderRadius: 2, display: 'flex', alignItems: 'center' }}><Typography>{tr('noPhoto')}</Typography></Box>}
        </Box>}
        <Box sx={{ minWidth: 0 }}>
          {showTitle && <Typography fontWeight={700}>{c.title}</Typography>}
          <Typography variant="body2" color="text.secondary">
            {publicQuoteDate(c.startDate, i18n.language)}
            {c.endDate && c.endDate !== c.startDate ? ` → ${publicQuoteDate(c.endDate, i18n.language)}` : ''}
          </Typography>
          {mapUrl && <Link href={mapUrl} target="_blank" rel="noreferrer">{location?.address || tr('map')}</Link>}
          {hotel && hasPhoto && <Button onClick={() => setPhotoOpen(true)} sx={{ display: 'block', mt: 1, px: 0 }}>{tr('viewHotelPhoto')}</Button>}
        </Box>
      </Stack>
      {hotel && <Dialog open={photoOpen} onClose={() => setPhotoOpen(false)} fullWidth maxWidth="md" aria-labelledby={`hotel-photo-${c.id}`}>
        <DialogTitle id={`hotel-photo-${c.id}`}>{c.title}</DialogTitle>
        <DialogContent>
          {hasPhoto ? <Box component="img" src={c.imageUrl} alt={c.title} referrerPolicy="no-referrer" onError={() => setFailedImage(c.imageUrl)} sx={{ width: '100%', maxHeight: '65dvh', objectFit: 'contain', borderRadius: 2 }} /> : <Typography>{tr('noPhoto')}</Typography>}
          {mapUrl && <Link href={mapUrl} target="_blank" rel="noreferrer">{location?.address || tr('map')}</Link>}
        </DialogContent>
        <DialogActions><Button onClick={() => setPhotoOpen(false)}>{tr('closePhoto')}</Button></DialogActions>
      </Dialog>}
      {c.insurance && <InsuranceCoverage coverage={c.insurance} />}
      {c.description && <Typography sx={{ whiteSpace: 'pre-line' }}>{c.description}</Typography>}
      {flight && (
        <Stack spacing={2}>
          {flight.fareFamily && <Chip label={flight.fareFamily} sx={{ alignSelf: 'flex-start' }} />}
          {flight.itineraries.map((i, index) => (
            <Box key={index} sx={{ border: '1px solid', borderColor: 'divider', p: 1.5, borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={700}>
                {publicQuoteDate(i.departureAt.slice(0, 10), i18n.language)} · {formatDuration(i.durationMinutes)} · {i.stops === 0 ? tr('directFlight') : tr('stopCount', { count: i.stops })}
              </Typography>
              {i.segments.map((s, j) => (
                <Stack key={j} spacing={0.5} sx={{ mt: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AirlineLogo code={s.marketingCarrier} />
                    <Typography fontWeight={600}>
                      {s.marketingCarrier} {s.flightNumber}
                    </Typography>
                    {s.operatingCarrier !== s.marketingCarrier && (
                      <Typography variant="caption">
                        {tr('operated')} {s.operatingCarrier}
                      </Typography>
                    )}
                  </Stack>
                  <Typography>
                    {s.origin.iata} {localTime(s.origin.at)} → {s.destination.iata} {localTime(s.destination.at)}
                    {dayOffsetLabel(dayOffset(s.origin.at, s.destination.at))}
                  </Typography>
                  <Typography variant="caption">
                    {publicQuoteDate(s.origin.at.slice(0, 10), i18n.language)} → {publicQuoteDate(s.destination.at.slice(0, 10), i18n.language)} · {s.cabin}
                    {s.durationMinutes ? ` · ${formatDuration(s.durationMinutes)}` : ''}
                  </Typography>
                  {(s.origin.terminal || s.destination.terminal) && (
                    <Typography variant="caption">
                      {tr('terminal')}: {s.origin.terminal || '—'} → {s.destination.terminal || '—'}
                    </Typography>
                  )}
                  {!flight.customerTerms?.baggage && s.includedBaggage.map((b, k) => (
                    <Typography key={k} variant="body2">
                      {tr(`bags.${b.type}.${b.quantity === 0 ? 'none' : 'included'}`, { count: b.quantity })}
                      {b.quantity > 0 && b.maxWeightKg ? ` · ${tr('bagWeight', { weight: b.maxWeightKg })}` : ''}
                    </Typography>
                  ))}
                  {typeof s.layoverMinutes === 'number' && s.layoverMinutes > 0 && (
                    <Typography color="text.secondary" variant="body2">
                      {tr('connection')} · {formatDuration(s.layoverMinutes)}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Box>
          ))}
          {flight.customerTerms?.baggage ? <Typography sx={{whiteSpace:'pre-line'}}>{flight.customerTerms.baggage}</Typography> : flight.baggageSummary?.map((b,index)=><Typography key={index} variant="body2">
            {tr(`bags.${b.type}.${!b.included?'none':b.quantity===undefined?'general':'included'}`, {count:b.quantity ?? 0})}
            {b.included && b.maxWeightKg ? ` · ${tr('bagWeight',{weight:b.maxWeightKg})}` : ''}
          </Typography>)}
          {flight.customerTerms && <Typography variant="body2" fontWeight={600}>{tr('customerTerms')}</Typography>}
          {(['refundable','changeable'] as const).map(kind=>{
            const value=flight.customerTerms?.[kind] ?? flight.conditions[kind];
            return typeof value==='boolean' ? <Typography key={kind} variant="body2">{tr(kind==='refundable'?'refund':'changes')}: {tr(value?'allowed':'notAllowed')}</Typography> : null;
          })}
          {(['refundPenalty', 'changePenalty'] as const).map((kind) => {
            const penalty = flight.conditions[kind];
            const overridden = flight.customerTerms?.[kind === 'refundPenalty' ? 'refundable' : 'changeable'] !== undefined;
            return penalty && !overridden ? (
              <Typography key={kind} variant="body2">
                {tr(kind)}: {penalty.currency ?? ''} {penalty.amount}
              </Typography>
            ) : null;
          })}
          {flight.separateTickets && <Typography color="warning.main">{tr('separateTickets')}</Typography>}
        </Stack>
      )}
      {c.facts.filter(Boolean).map((fact, i) => (
        <Typography key={i} variant="body2">
          {fact}
        </Typography>
      ))}
      {c.days?.length ? (
        <Box component="details">
          <Box component="summary" sx={{ cursor: 'pointer', fontWeight: 600 }}>
            {tr('itinerary')}
          </Box>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {c.days.map((day, i) => (
              <Box key={i}>
                <Typography fontWeight={600}>
                  {tr('day')} {day.day} {c.startDate ? `· ${shiftQuoteDate(c.startDate, day.day - 1)}` : ''} ·{' '}
                  {day.title}
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-line' }}>{day.description}</Typography>
                {day.detail && <Typography sx={{ whiteSpace: 'pre-line' }}>{day.detail}</Typography>}
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}
      {c.includes?.length ? (
        <Box>
          <Typography fontWeight={600}>{tr('includes')}</Typography>
          {c.includes.map((item, i) => (
            <Typography key={i} variant="body2">
              {item}
            </Typography>
          ))}
        </Box>
      ) : null}
      {c.conditions.length > 0 && (
        <Box component="details">
          <Box component="summary" sx={{ cursor: 'pointer' }}>
            {tr('conditions')}
          </Box>
          {c.conditions.map((condition, i) => (
            <Typography key={i} variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {condition}
            </Typography>
          ))}
        </Box>
      )}
    </Stack>
  );
}
